import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { useBankList } from "../../../../../../app/hooks/useBankList";
import { savedAsiaPayoutData } from "../../../../redux/formDataSlice";
import { ContentWrapper, InputWrapper, Error, ItemWrapper, ButtonsWrapper } from "../../../shared/styles";
import { Input } from "../../../../../../components/common/Input";
import CustomSelect from "../../../../../../components/common/CustomSelect";
import { Button } from "../../../../../../components/common/Button";
import Loader from "../../../../../../components/common/Loader";
import { CountryConfig } from "../../../shared/types";
import { FormInputs } from "../../../../../../types/walletTypes";
import { UAE_BENEFICIARY_TYPE_OPTIONS } from "../../../shared/const";

interface UAEBeneficiaryFormProps {
  config?: CountryConfig;
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const UAEBeneficiaryForm: React.FC<UAEBeneficiaryFormProps> = ({ handleNext, handlePrevious }) => {
  const dispatch = useAppDispatch();
  const asiaPayoutData: FormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.aed || {}
  );
  const { businessId } = useAppSelector((state) => state.users);

  const { banks, selectBanks, loading: banksLoading } = useBankList({
    country: "AED",
    businessId,
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    defaultValues: {
      beneficiaryType: asiaPayoutData?.beneficiaryType,
      accountHolderName: asiaPayoutData?.accountHolderName,
      bankName: asiaPayoutData?.bankName,
      iban: asiaPayoutData?.iban,
      swiftCode: asiaPayoutData?.swiftCode,
      receiverAddress: asiaPayoutData?.receiverAddress,
      city: asiaPayoutData?.city,
      state: asiaPayoutData?.state,
      country: asiaPayoutData?.country || "United Arab Emirates",
      postalCode: asiaPayoutData?.postalCode,
    },
    mode: "onChange",
  });

  const watchBankName = watch("bankName");

  useEffect(() => {
    if (watchBankName && banks && banks.length > 0) {
      const selectedBank = (banks as any[]).find(
        (b: any) => b.name === watchBankName
      );
      
      if (selectedBank) {
        const swift = selectedBank.swift_code;
        if (swift) {
          setValue("swiftCode", swift, { shouldValidate: true });
        }
      }
    }
  }, [watchBankName, banks, setValue]);

  const onSubmit = (data: FormInputs) => {
    const updatedData = {
      ...asiaPayoutData,
      ...data,
    };
    dispatch(savedAsiaPayoutData({ country: "aed", data: updatedData }));
    handleNext?.();
  };

  return (
    <ContentWrapper>
      <Loader isLoading={banksLoading} />

      <InputWrapper>
        <Controller
          name="beneficiaryType"
          control={control}
          rules={{ required: "Beneficiary type is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              value={field.value}
              onChange={field.onChange}
              options={UAE_BENEFICIARY_TYPE_OPTIONS}
              placeholder="Beneficiary type"
              lightBorder
            />
          )}
        />
        {errors.beneficiaryType?.message && (
          <Error>{String(errors.beneficiaryType.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="accountHolderName"
          control={control}
          rules={{ required: "Full name is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Full name"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors.accountHolderName}
            />
          )}
        />
        {errors.accountHolderName?.message && (
          <Error>{String(errors.accountHolderName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="bankName"
          control={control}
          rules={{ required: "Bank name is required" }}
          render={({ field }) => (
            <CustomSelect
              $width="100%"
              value={field.value}
              onChange={field.onChange}
              options={selectBanks.map((b) => ({ label: b.label, value: b.label }))}
              placeholder={banksLoading ? "Loading banks..." : "Bank name"}
              lightBorder
            />
          )}
        />
        {errors.bankName?.message && (
          <Error>{String(errors.bankName.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="iban"
          control={control}
          rules={{
            required: "IBAN is required",
            pattern: {
              value: /^AE\d{21}$/,
              message: "Enter a valid UAE IBAN (e.g. AE070331234567890123456)",
            },
          }}
          render={({ field }) => (
            <Input
              type="text"
              label="IBAN"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors.iban}
            />
          )}
        />
        {errors.iban?.message && <Error>{String(errors.iban.message)}</Error>}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="swiftCode"
          control={control}
          rules={{
            required: "Swift code is required",
          }}
          render={({ field }) => (
            <Input
              type="text"
              label="Swift code"
              value={field.value || ""}
              onChange={field.onChange}
              disabled={true}
              hasError={!!errors.swiftCode}
            />
          )}
        />
        {errors.swiftCode?.message && (
          <Error>{String(errors.swiftCode.message)}</Error>
        )}
      </InputWrapper>

      <InputWrapper>
        <Controller
          name="receiverAddress"
          control={control}
          rules={{ required: "Beneficiary address is required" }}
          render={({ field }) => (
            <Input
              type="text"
              label="Beneficiary address"
              value={field.value || ""}
              onChange={field.onChange}
              hasError={!!errors.receiverAddress}
            />
          )}
        />
        {errors.receiverAddress?.message && (
          <Error>{String(errors.receiverAddress.message)}</Error>
        )}
      </InputWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="city"
            control={control}
            rules={{ required: "City is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="City"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors.city}
              />
            )}
          />
          {errors.city?.message && <Error>{String(errors.city.message)}</Error>}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="state"
            control={control}
            rules={{ required: "State is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="State"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors.state}
              />
            )}
          />
          {errors.state?.message && <Error>{String(errors.state.message)}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <ItemWrapper>
        <InputWrapper>
          <Controller
            name="country"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field }) => (
              <CustomSelect
                $width="100%"
                value={field.value}
                onChange={field.onChange}
                options={[{ label: "United Arab Emirates", value: "United Arab Emirates" }]}
                placeholder="Country"
                lightBorder
              />
            )}
          />
          {errors.country?.message && <Error>{String(errors.country.message)}</Error>}
        </InputWrapper>

        <InputWrapper>
          <Controller
            name="postalCode"
            control={control}
            rules={{ required: "Postal Code is required" }}
            render={({ field }) => (
              <Input
                type="text"
                label="Postal Code"
                value={field.value || ""}
                onChange={field.onChange}
                hasError={!!errors.postalCode}
              />
            )}
          />
          {errors.postalCode?.message && <Error>{String(errors.postalCode.message)}</Error>}
        </InputWrapper>
      </ItemWrapper>

      <ButtonsWrapper>
        <Button onClick={handlePrevious} theme="alternate" height="48px">
          Back
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={!isValid} label="Continue" height="48px" />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default UAEBeneficiaryForm;
